import { defineAsyncComponent, markRaw, reactive } from "vue";
import router from "@/router";
import { loadModule } from "@/service/loadModule";
import ar from "@/i18n/ar";
import en from "@/i18n/en";
import geteRelativeUrl from "@/getUrl";
import app from "./app";
import axios from "axios";
import navigation from "@/router/navigation";
import application from "@/router/application";
import roles from "@/router/roles";

export default reactive({
  widget: [],
  toolbar: [],
  tasks: [],
  routesLoaded: false,
  async init() {
    try {
      this.initVariable();
      await this.loadScript();
      this.routesLoaded = true;
    } catch (err) {
      console.warn("Plugins initialization notice:", err);
      this.routesLoaded = true;
    }
  },
  async loadScript() {
    const { data } = await axios.get(`plugins/script`);

    // Create promises for all script loads
    const scriptPromises = data.map(element => {
      return new Promise((resolve, reject) => {
        try {
          let ele = document.createElement("script");
          ele.async = true;
          ele.src = `${geteRelativeUrl()}plugins/file?path=${element.path.join()}`;
          document.body.append(ele);

          ele.onload = async () => {
            try {
              // Ensure the plugin function is called with proper async handling
              const pluginFunction = plugins[element.path[0]];
              if (pluginFunction) {
                await pluginFunction.call(this, element.info);
              }
              resolve();
            } catch (err) {
              console.log('Plugin initialization error:', err);
              reject(err);
            }
          };

          ele.onerror = (error) => {
            console.log('Script loading error:', error);
            reject(error);
          };
        } catch (err) {
          console.log('Script creation error:', err);
          reject(err);
        }
      });
    });

    // Wait for all scripts to load and initialize
    try {
      await Promise.allSettled(scriptPromises);
    } catch (err) {
      console.log('Error loading some scripts:', err);
    }
  },
  initVariable() {
    this.toolbar = [];
    this.widget = [];
    this.tasks = [];
  },
  addWidget(item) {
    this.widget.push(
      markRaw(defineAsyncComponent(() => loadModule(item.file, item.name)))
    );
  },
  addTask(item) {
    this.tasks.push(
      markRaw(defineAsyncComponent(() => loadModule(item.file, item.name)))
    );
  },
  addToExplorer(data) {
    application[data.id] = data;
  },
  addLanguage(code, key, value) {
    switch (code) {
      case "ar":
        if (ar[key] != undefined) return;
        ar[key] = value;
        break;
      case "en":
        if (en[key] != undefined) return;
        en[key] = value;
        break;
    }
  },
  addToToolbar(route) {
    this.toolbar.push(route);
  },
  addToSetting(route) {
    if (navigation["System"] && navigation["System"].items && navigation["System"].items["/app/setting"]) {
      navigation["System"].items["/app/setting"].items.push(route);
    }
  },
  addToDashboard(cat, item) {
    if (!navigation[cat]) {
      navigation[cat] = { label: cat, items: {} };
    }
    if (!navigation[cat].items) {
      navigation[cat].items = {};
    }
    navigation[cat].items[item.to] = item;
    if (!app.adminRoute[cat]) {
      app.adminRoute[cat] = [];
    }
    if (!app.adminRoute[cat].includes(item.to)) {
      app.adminRoute[cat].push(item.to);
    }
  },
  addToDashboardRoot(cat, item, routes = []) {
    // In-place reorder - no intermediate object created
    const reorderInPlace = (obj, key, value, targetOrder) => {
      const keys = Object.keys(obj);
      const existingIdx = keys.indexOf(key);

      // Remove existing if present
      if (existingIdx > -1) {
        delete obj[key];
        keys.splice(existingIdx, 1);
      }

      const insertIdx = Math.max(0, Math.min(targetOrder, keys.length));

      // Only store keys that need to shift (from insertIdx to end)
      const len = keys.length;
      const shiftCount = len - insertIdx;

      if (shiftCount > 0) {
        const shiftedKeys = new Array(shiftCount);
        const shiftedVals = new Array(shiftCount);

        for (let i = insertIdx; i < len; i++) {
          const k = keys[i];
          shiftedKeys[i - insertIdx] = k;
          shiftedVals[i - insertIdx] = obj[k];
          delete obj[k];
        }

        obj[key] = value;

        for (let i = 0; i < shiftCount; i++) {
          obj[shiftedKeys[i]] = shiftedVals[i];
        }
      } else {
        obj[key] = value;
      }
    };

    if (item.order !== undefined) {
      reorderInPlace(navigation, cat, item, item.order);
      reorderInPlace(app.adminRoute, cat, routes, item.order);
    } else {
      navigation[cat] = item;
      app.adminRoute[cat] = routes;
    }
  },
  addRole(role) {
    roles[role.id] = role;
  },
  addPage(page, to = "app") {
    try {
      // Check if route already exists to avoid duplicates
      const existingRoute = router.hasRoute(page.name);
      if (existingRoute) {
        console.warn(`Route ${page.name} already exists, skipping...`);
        return;
      }

      // Add the route
      router.addRoute(to, {
        name: page.name,
        path: page.path,
        component: defineAsyncComponent(() => loadModule(page.file, page.name)),
        meta: {
          isDynamic: true,
          pluginRoute: true
        }
      });

    } catch (error) {
      console.error(`Error adding route ${page.name}:`, error);
    }
  },
});
